import { getAuthenticatedClient } from "../supabaseClient";

/**
 * @param {string | { address?: unknown, latitude?: unknown, longitude?: unknown }} entry
 * @returns {{ address: string, latitude: number | null, longitude: number | null }}
 */
function normalizeLocationEntry(entry) {
  if (entry && typeof entry === "object") {
    const latitude =
      entry.latitude != null && entry.latitude !== "" ? Number(entry.latitude) : null;
    const longitude =
      entry.longitude != null && entry.longitude !== "" ? Number(entry.longitude) : null;
    return {
      address: entry.address != null ? String(entry.address) : "",
      latitude: Number.isFinite(latitude) ? latitude : null,
      longitude: Number.isFinite(longitude) ? longitude : null,
    };
  }

  return {
    address: entry != null ? String(entry) : "",
    latitude: null,
    longitude: null,
  };
}

/**
 * Upsert confirmed Geocodio locations and drop sites that are no longer in the fleet.
 *
 * @param {string} userId
 * @param {Record<string, string | { address?: unknown, latitude?: unknown, longitude?: unknown }>} domicileAddressMap
 * @returns {Promise<{ error?: string }>}
 */
export async function upsertSiteLocations(userId, domicileAddressMap) {
  const supabase = getAuthenticatedClient();
  const rows = [];

  for (const [domicile, entry] of Object.entries(domicileAddressMap ?? {})) {
    const name = String(domicile ?? "").trim();
    if (!name) continue;
    const location = normalizeLocationEntry(entry);
    rows.push({
      user_id: userId,
      domicile: name,
      address: location.address,
      latitude: location.latitude,
      longitude: location.longitude,
    });
  }

  if (rows.length === 0) {
    return {};
  }

  const { error: upsertError } = await supabase
    .from("site_locations")
    .upsert(rows, { onConflict: "user_id,domicile" });
  if (upsertError) {
    return { error: upsertError.message };
  }

  const keep = new Set(rows.map((row) => row.domicile));
  const { data: existing, error: selectError } = await supabase
    .from("site_locations")
    .select("id, domicile")
    .eq("user_id", userId);
  if (selectError) {
    return { error: selectError.message };
  }

  const staleIds = (existing ?? [])
    .filter((row) => !keep.has(row.domicile))
    .map((row) => row.id);
  if (staleIds.length === 0) {
    return {};
  }

  const { error: deleteError } = await supabase
    .from("site_locations")
    .delete()
    .in("id", staleIds);
  if (deleteError) {
    return { error: deleteError.message };
  }

  return {};
}
